/**
 * RecursoModal
 * Modal para crear/editar recursos
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { recursoService, partidaService, areaService, tipoRecursoService } from '../services/catalogoService';
import './RecursoModal.css';

const RecursoModal = ({ isOpen, mode = 'create', recurso = null, partidaId = null, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    unidad: 'Un',
    stock: false,
    tipo_recurso: '',
    cantidad_presupuesto: 0,
    precio_unitario_costo: 0,
    markup: 0,
    precio_unitario_venta: 0,
    area: '',
    partida: partidaId || ''
  });
  const [areas, setAreas] = useState([]);
  const [partidas, setPartidas] = useState([]);
  const [tiposRecurso, setTiposRecurso] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      cargarAreas();
      cargarTiposRecurso();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.area) {
      cargarPartidas(formData.area);
    }
  }, [formData.area]);

  useEffect(() => {
    if (mode === 'edit' && recurso) {
      setFormData({
        nombre: recurso.nombre || '',
        descripcion: recurso.descripcion || '',
        unidad: recurso.unidad || 'Un',
        stock: recurso.stock || false,
        tipo_recurso: recurso.tipo_recurso || '',
        cantidad_presupuesto: recurso.cantidad_presupuesto || 0,
        precio_unitario_costo: recurso.precio_unitario_costo || 0,
        markup: recurso.markup || 0,
        precio_unitario_venta: recurso.precio_unitario_venta || 0,
        partida: recurso.partida || '',
        area: ''
      });
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        unidad: 'Un',
        stock: false,
        tipo_recurso: '',
        cantidad_presupuesto: 0,
        precio_unitario_costo: 0,
        markup: 0,
        precio_unitario_venta: 0,
        area: '',
        partida: partidaId || ''
      });
    }
  }, [mode, recurso, partidaId, isOpen]);

  const cargarAreas = async () => {
    try {
      const response = await areaService.getAll({ status: 'activo' });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setAreas(data);
    } catch (error) {
      console.error('Error al cargar áreas:', error);
      toast.error('Error al cargar áreas');
    }
  };

  const cargarTiposRecurso = async () => {
    try {
      const response = await tipoRecursoService.getAll();
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setTiposRecurso(data);
    } catch (error) {
      console.error('Error al cargar tipos de recurso:', error);
      toast.error('Error al cargar tipos de recurso');
    }
  };

  const cargarPartidas = async (areaId) => {
    try {
      const response = await partidaService.getByArea(areaId, { status: 'activo' });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setPartidas(data);
    } catch (error) {
      console.error('Error al cargar partidas:', error);
      toast.error('Error al cargar partidas');
    }
  };

  // Calcula precio venta a partir de costo + markup
  const calcularPrecioVenta = (costo, markup) => {
    const c = parseFloat(costo) || 0;
    const m = parseFloat(markup) || 0;
    return Math.round(c * (1 + m / 100));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    const numericFields = ['cantidad_presupuesto', 'precio_unitario_costo', 'markup', 'precio_unitario_venta'];
    const numValue = numericFields.includes(name) ? (parseFloat(value) || 0) : value;

    if (name === 'area') {
      setFormData(prev => ({ ...prev, area: value, partida: '' }));
    } else if (name === 'precio_unitario_costo') {
      const newCosto = parseFloat(value) || 0;
      setFormData(prev => ({
        ...prev,
        precio_unitario_costo: newCosto,
        precio_unitario_venta: calcularPrecioVenta(newCosto, prev.markup)
      }));
    } else if (name === 'markup') {
      const newMarkup = parseFloat(value) || 0;
      setFormData(prev => ({
        ...prev,
        markup: newMarkup,
        precio_unitario_venta: calcularPrecioVenta(prev.precio_unitario_costo, newMarkup)
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: numValue }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.error('El nombre del recurso es requerido');
      return;
    }
    if (!formData.partida) {
      toast.error('Debes seleccionar una partida');
      return;
    }
    if (formData.cantidad_presupuesto < 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }
    if (formData.precio_unitario_costo < 0) {
      toast.error('El precio unitario de costo debe ser mayor a 0');
      return;
    }
    if (formData.precio_unitario_venta < 0) {
      toast.error('El precio unitario de venta debe ser mayor a 0');
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        unidad: formData.unidad,
        stock: formData.stock,
        tipo_recurso: formData.tipo_recurso || null,
        cantidad_presupuesto: formData.cantidad_presupuesto,
        precio_unitario_costo: formData.precio_unitario_costo,
        markup: formData.markup,
        precio_unitario_venta: formData.precio_unitario_venta,
        partida: formData.partida
      };

      if (mode === 'create') {
        await recursoService.create({ ...dataToSend, status: 'activo' });
        toast.success('Recurso creado exitosamente');
      } else if (mode === 'edit') {
        await recursoService.update(recurso.id, dataToSend);
        toast.success('Recurso actualizado exitosamente');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error al guardar recurso:', error);
      toast.error('Error al guardar el recurso');
    } finally {
      setLoading(false);
    }
  };

  const subtotalCosto = (formData.cantidad_presupuesto * formData.precio_unitario_costo);
  const subtotalVenta = (formData.cantidad_presupuesto * formData.precio_unitario_venta);
  const utilidad = subtotalVenta - subtotalCosto;

  const formatCLP = (val) => `$${Math.round(val).toLocaleString('es-CL')}`;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-presupuesto modal-recurso-wide" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{mode === 'create' ? 'Agregar Recurso al Presupuesto' : 'Editar Recurso'}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="presupuesto-form-modern">
          {/* Sección: Ubicación (solo si no viene partidaId) */}
          {!partidaId && (
            <div className="pf-section">
              <div className="pf-section-header">
                <span className="pf-section-icon">📍</span>
                <h4>Ubicación</h4>
              </div>
              <div className="pf-section-body">
                <div className="pf-grid-2">
                  <div className="form-group">
                    <label className="pf-label">Área *</label>
                    <select
                      name="area"
                      value={formData.area}
                      onChange={handleChange}
                      disabled={mode === 'edit'}
                      className="form-control"
                    >
                      <option value="">Selecciona un área</option>
                      {areas.map(area => (
                        <option key={area.id} value={area.id}>{area.nombre}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="pf-label">Partida *</label>
                    <select
                      name="partida"
                      value={formData.partida}
                      onChange={handleChange}
                      disabled={mode === 'edit' || !formData.area}
                      className="form-control"
                    >
                      <option value="">Selecciona una partida</option>
                      {partidas.map(partida => (
                        <option key={partida.id} value={partida.id}>{partida.nombre}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sección: Información General */}
          <div className="pf-section">
            <div className="pf-section-header">
              <span className="pf-section-icon">📋</span>
              <h4>Información General</h4>
            </div>
            <div className="pf-section-body">
              <div className="pf-grid-2">
                <div className="form-group">
                  <label className="pf-label">Nombre *</label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Nombre del recurso"
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label className="pf-label">Unidad de Medida *</label>
                  <select
                    name="unidad"
                    value={formData.unidad}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="Hr">Horas</option>
                    <option value="Un">Unidades</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="pf-label">Descripción</label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  placeholder="Descripción (opcional)"
                  rows="2"
                  className="form-control"
                />
              </div>

              <div className="pf-grid-2">
                <div className="form-group">
                  <label className="pf-label">Tipo de Recurso</label>
                  <select
                    name="tipo_recurso"
                    value={formData.tipo_recurso}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="">Selecciona un tipo (opcional)</option>
                    {tiposRecurso.map(tipo => (
                      <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <label className="pf-toggle-label" style={{ paddingBottom: 0 }}>
                    <div className="pf-toggle">
                      <input
                        type="checkbox"
                        name="stock"
                        checked={formData.stock}
                        onChange={handleChange}
                      />
                      <span className="pf-toggle-slider"></span>
                    </div>
                    <span>Control de stock</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Sección: Montos */}
          <div className="pf-section pf-section-montos">
            <div className="pf-section-header">
              <span className="pf-section-icon">💰</span>
              <h4>Montos</h4>
            </div>
            <div className="pf-section-body">
              <div className="pf-grid-3">
                <div className="form-group">
                  <label className="pf-label">Cantidad *</label>
                  <input
                    type="number"
                    name="cantidad_presupuesto"
                    value={formData.cantidad_presupuesto}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label className="pf-label">Precio Costo (CLP)</label>
                  <div className="pf-input-decorated">
                    <span className="pf-input-prefix">$</span>
                    <input
                      type="number"
                      name="precio_unitario_costo"
                      value={formData.precio_unitario_costo}
                      onChange={handleChange}
                      step="1"
                      min="0"
                      className="form-control pf-has-prefix"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="pf-label">Markup (%)</label>
                  <div className="pf-input-decorated">
                    <input
                      type="number"
                      name="markup"
                      value={formData.markup}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      max="1000"
                      className="form-control"
                    />
                    <span className="pf-input-suffix">%</span>
                  </div>
                </div>
              </div>

              {/* Precio Venta calculado */}
              <div className="pf-calculated-field">
                <div className="pf-calculated-label">
                  <span>Precio Venta (CLP) *</span>
                  <span className="pf-calculated-badge">Auto-calculado</span>
                </div>
                <div className="pf-calculated-value">
                  {formatCLP(formData.precio_unitario_venta)}
                </div>
                <div className="pf-calculated-formula">
                  {formatCLP(formData.precio_unitario_costo)} + {formData.markup}% markup = {formatCLP(formData.precio_unitario_venta)}
                </div>
              </div>

              {/* Resumen de subtotales */}
              {formData.cantidad_presupuesto > 0 && formData.precio_unitario_costo > 0 && (
                <div className="pf-subtotales">
                  <div className="pf-subtotal-row">
                    <span>Subtotal Costo</span>
                    <span>{formatCLP(subtotalCosto)}</span>
                  </div>
                  <div className="pf-subtotal-row">
                    <span>Subtotal Venta</span>
                    <span className="pf-subtotal-venta">{formatCLP(subtotalVenta)}</span>
                  </div>
                  <div className={`pf-subtotal-row pf-subtotal-utilidad ${utilidad >= 0 ? 'positivo' : 'negativo'}`}>
                    <span>Utilidad</span>
                    <span>{formatCLP(utilidad)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Guardando...' : mode === 'create' ? 'Crear Recurso' : 'Actualizar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecursoModal;
